import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], saved: [], subtotal: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) { setCart({ items: [], saved: [], subtotal: 0 }); return; }
    try {
      const r = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(r.data);
    } catch { setCart({ items: [], saved: [], subtotal: 0 }); }
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, choices = {}) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/cart/items`, { product_id: productId, quantity, ...choices }, { withCredentials: true });
      setCart(r.data);
      return true;
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not add to cart');
      return false;
    } finally { setLoading(false); }
  };

  const updateQuantity = async (lineId, quantity) => {
    try {
      const r = await axios.put(`${API}/cart/items/${encodeURIComponent(lineId)}`, { quantity }, { withCredentials: true });
      setCart(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not update quantity');
    }
  };

  // Save-for-later list: move lines between the cart and the saved list
  const savedAction = async (method, path) => {
    try {
      const r = await axios[method](`${API}${path}`, method === 'delete' ? { withCredentials: true } : {}, { withCredentials: true });
      setCart(r.data);
      return true;
    } catch (e) {
      alert(e.response?.data?.detail || 'Something went wrong');
      return false;
    }
  };
  const saveForLater = (lineId) => savedAction('post', `/cart/items/${encodeURIComponent(lineId)}/save-for-later`);
  const moveToCart = (lineId) => savedAction('post', `/cart/saved/${encodeURIComponent(lineId)}/move-to-cart`);
  const removeSaved = (lineId) => savedAction('delete', `/cart/saved/${encodeURIComponent(lineId)}`);

  const clearCart = async () => {
    await axios.delete(`${API}/cart`, { withCredentials: true });
    setCart(c => ({ ...c, items: [], subtotal: 0 }));
  };

  const itemCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, clearCart, fetchCart, saveForLater, moveToCart, removeSaved, loading, itemCount }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
